import { useState, useEffect } from 'react'
import { useGrantTenantFeature } from '@/hooks/useTenantFeatures'
import { useFeatures } from '@/hooks/useAdminFeatures'

interface GrantFeatureModalProps {
  tenantId: string
  tenantName: string
  onClose: () => void
  onSuccess?: () => void
  // Optional: pre-select a specific feature
  preSelectedFeatureKey?: string
}

const QUICK_EXPIRY_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
]

export default function GrantFeatureModal({
  tenantId,
  tenantName,
  onClose,
  onSuccess,
  preSelectedFeatureKey,
}: GrantFeatureModalProps) {
  const { data: availableFeatures, isLoading: featuresLoading } = useFeatures()
  const grantMutation = useGrantTenantFeature()

  const [selectedFeatureKey, setSelectedFeatureKey] = useState<string>('')
  const [expiryType, setExpiryType] = useState<'permanent' | 'temporary'>('temporary')
  const [customDays, setCustomDays] = useState<number>(30)
  const [selectedQuickOption, setSelectedQuickOption] = useState<number>(30)
  const [error, setError] = useState<string>('')

  // Pre-select feature if provided
  useEffect(() => {
    if (preSelectedFeatureKey) {
      setSelectedFeatureKey(preSelectedFeatureKey)
    }
  }, [preSelectedFeatureKey])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!selectedFeatureKey) {
      setError('Please select a feature')
      return
    }

    if (expiryType === 'temporary' && customDays <= 0) {
      setError('Expiration must be at least 1 day')
      return
    }

    try {
      await grantMutation.mutateAsync({
        tenantId,
        featureKey: selectedFeatureKey,
        expiresInDays: expiryType === 'permanent' ? null : customDays,
      })

      onSuccess?.()
      onClose()
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to grant feature'
      setError(errorMessage)
    }
  }

  const selectedFeature = availableFeatures?.find((f) => f.key === selectedFeatureKey)

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div
          className="bg-[#0a0e14] border border-[#FFB84D]/30 rounded-lg shadow-[0_0_50px_rgba(255,184,77,0.2)]
                     w-full max-w-lg my-8"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-[#FFB84D]/20 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">✨</span>
                <div>
                  <h2 className="text-xl font-mono uppercase tracking-wider text-[#FFB84D]">
                    Grant Feature Override
                  </h2>
                  <div className="text-xs text-[#8b949e] font-mono mt-1">
                    Tenant: {tenantName}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-[#8b949e] hover:text-[#FFB84D] transition-colors text-2xl"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Feature Selection */}
            <div>
              <label className="block text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">
                Select Feature <span className="text-red-500">*</span>
              </label>
              {featuresLoading ? (
                <div className="text-xs text-[#8b949e] font-mono">Loading features...</div>
              ) : (
                <select
                  value={selectedFeatureKey}
                  onChange={(e) => setSelectedFeatureKey(e.target.value)}
                  className="w-full bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                             text-[#e8eaed] font-mono text-sm
                             focus:outline-none focus:border-[#FFB84D]/50"
                  required
                >
                  <option value="">-- Select a feature --</option>
                  {availableFeatures?.map((feature) => (
                    <option key={feature.key} value={feature.key}>
                      {feature.name} ({feature.key})
                    </option>
                  ))}
                </select>
              )}

              {/* Feature Description */}
              {selectedFeature && (
                <div className="mt-2 p-3 bg-[#141c24]/60 border border-[#2a3542] rounded">
                  <div className="text-xs text-[#8b949e] font-mono mb-1">
                    Category: <span className="text-[#FFB84D]">{selectedFeature.category}</span>
                  </div>
                  <div className="text-xs text-[#8b949e] font-mono">
                    {selectedFeature.description}
                  </div>
                </div>
              )}
            </div>

            {/* Expiry Type */}
            <div>
              <label className="block text-xs text-[#8b949e] font-mono mb-3 uppercase tracking-wider">
                Access Duration
              </label>
              <div className="space-y-3">
                {/* Permanent */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="expiryType"
                    value="permanent"
                    checked={expiryType === 'permanent'}
                    onChange={() => setExpiryType('permanent')}
                    className="mt-0.5 w-4 h-4 bg-[#141c24] border border-[#2a3542]
                               checked:bg-[#FFB84D] checked:border-[#FFB84D]
                               focus:outline-none focus:ring-2 focus:ring-[#FFB84D]/50"
                  />
                  <div>
                    <div className="text-sm text-[#e8eaed] font-mono group-hover:text-[#FFB84D]">
                      Permanent
                    </div>
                    <div className="text-xs text-[#8b949e] font-mono">
                      Grant indefinite access to this feature
                    </div>
                  </div>
                </label>

                {/* Temporary */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="expiryType"
                    value="temporary"
                    checked={expiryType === 'temporary'}
                    onChange={() => setExpiryType('temporary')}
                    className="mt-0.5 w-4 h-4 bg-[#141c24] border border-[#2a3542]
                               checked:bg-[#FFB84D] checked:border-[#FFB84D]
                               focus:outline-none focus:ring-2 focus:ring-[#FFB84D]/50"
                  />
                  <div className="flex-1">
                    <div className="text-sm text-[#e8eaed] font-mono group-hover:text-[#FFB84D] mb-2">
                      Temporary (Trial Period)
                    </div>

                    {expiryType === 'temporary' && (
                      <div className="space-y-3 ml-1">
                        {/* Quick Options */}
                        <div className="flex gap-2">
                          {QUICK_EXPIRY_OPTIONS.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setSelectedQuickOption(option.value)
                                setCustomDays(option.value)
                              }}
                              className={`px-3 py-1.5 rounded text-xs font-mono transition-all
                                ${selectedQuickOption === option.value
                                  ? 'bg-[#FFB84D] text-[#0a0e14] font-bold'
                                  : 'bg-[#141c24] text-[#8b949e] border border-[#2a3542] hover:border-[#FFB84D]/30'
                                }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>

                        {/* Custom Days Input */}
                        <div>
                          <label className="block text-xs text-[#8b949e] font-mono mb-1">
                            Or specify custom days:
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={customDays}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 1
                              setCustomDays(val)
                              setSelectedQuickOption(-1)
                            }}
                            className="w-full bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                                       text-[#e8eaed] font-mono text-sm
                                       focus:outline-none focus:border-[#FFB84D]/50"
                            placeholder="e.g., 60"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Info Notice */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3">
              <div className="text-xs text-blue-400 font-mono">
                ℹ️ This will create a feature override with source "override" for this tenant.
                {expiryType === 'temporary' && (
                  <> The override will expire in {customDays} day{customDays !== 1 ? 's' : ''}.</>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-900/20 border border-red-500/30 rounded p-3">
                <div className="text-sm text-red-400 font-mono">{error}</div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#2a3542]">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 bg-[#141c24] border border-[#2a3542] rounded
                           text-[#8b949e] font-mono text-sm uppercase tracking-wider
                           hover:border-[#FFB84D]/30 hover:text-[#FFB84D]
                           transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={grantMutation.isPending}
                className="px-5 py-2 bg-[#FFB84D] rounded
                           text-[#0a0e14] font-mono text-sm uppercase tracking-wider font-bold
                           hover:bg-[#FFB84D]/90 hover:shadow-[0_0_20px_rgba(255,184,77,0.3)]
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200"
              >
                {grantMutation.isPending ? 'Granting...' : 'Grant Access'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
